/**
 * Bet actions for one panel.
 *
 * The rule that shapes this whole hook: a cashout is only real when the SERVER
 * acks it. The button goes into a pending state, waits for the ack, and shows
 * the server error code otherwise — it never flips to "cashed" on its own.
 */
import { useCallback, useRef, useState } from "react";
import { MyBet, type BetPanel, type ErrorCode } from "@crashx/shared";
import { getConnection, multiplierModel, serverNow } from "../../net/connection.js";
import { selectCanBet, useConnectionStore } from "../../state/connectionStore.js";
import { selectActiveBet, useRoundStore } from "../../state/roundStore.js";

export interface BetActions {
  activeBet: ReturnType<ReturnType<typeof selectActiveBet>>;
  pending: boolean;
  error: ErrorCode | null;
  clearError: () => void;
  place: (amount: number, currency: string, autoCashout: number | null) => Promise<MyBet | null>;
  cancel: () => Promise<void>;
  cashout: () => Promise<void>;
}

export function useBetActions(panel: BetPanel): BetActions {
  const activeBet = useRoundStore(selectActiveBet(panel));
  const busy = useRef(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<ErrorCode | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const place = useCallback(
    async (amount: number, currency: string, autoCashout: number | null) => {
      if (busy.current || !selectCanBet(useConnectionStore.getState())) return null;
      busy.current = true;
      setPending(true);
      setError(null);
      try {
        const ack = await getConnection().placeBet({ panel, amount, currency, autoCashout });
        if (!ack.ok) {
          setError(ack.error.code);
          return null;
        }
        const parsed = MyBet.safeParse(ack.data);
        if (!parsed.success) {
          setError("INTERNAL");
          return null;
        }
        return parsed.data;
      } catch {
        setError("INTERNAL");
        return null;
      } finally {
        busy.current = false;
        setPending(false);
      }
    },
    [panel],
  );

  const cancel = useCallback(async () => {
    if (activeBet === null || busy.current || !selectCanBet(useConnectionStore.getState())) return;
    busy.current = true;
    setPending(true);
    setError(null);
    try {
      const ack = await getConnection().cancelBet(activeBet.betId);
      if (!ack.ok) setError(ack.error.code);
    } catch {
      setError("INTERNAL");
    } finally {
      busy.current = false;
      setPending(false);
    }
  }, [activeBet]);

  const cashout = useCallback(async () => {
    if (activeBet === null || busy.current || !selectCanBet(useConnectionStore.getState())) return;
    busy.current = true;
    setPending(true);
    setError(null);
    try {
      // Informational only: the server recomputes it from its own clock.
      const observed = multiplierModel.valueAt(serverNow());
      const ack = await getConnection().cashout(activeBet.betId, observed);
      if (!ack.ok) setError(ack.error.code);
    } catch {
      setError("INTERNAL");
    } finally {
      busy.current = false;
      setPending(false);
    }
  }, [activeBet]);

  return { activeBet, pending, error, clearError, place, cancel, cashout };
}
